import { useEffect, useMemo } from "react";
import SysRecord from "app/class/record";
import {
    eventMatchesActionHotkey,
    getActionShortcut,
    hotkeyToSignature,
    isEditableHotkeyTarget,
    normalizeActionHotkey,
} from "modules/record/shared/services/actionHotkeys";

interface RegisteredHotkeyAction {
    action: SysRecord;
    hotkey: NonNullable<ReturnType<typeof normalizeActionHotkey>>;
    signature: string;
    isDisabled: () => boolean;
    trigger: () => void;
}

interface UseActionHotkeysOptions {
    actions: SysRecord[];
    disabled?: boolean;
    runningActionId?: string | null;
    getActionDisabled?: (action: SysRecord) => boolean;
    onActionTrigger: (action: SysRecord) => void;
}

let registeredHotkeyActions: RegisteredHotkeyAction[] = [];
let isGlobalListenerRegistered = false;

const handleGlobalKeyDown = (event: KeyboardEvent) => {
    if (event.repeat || !registeredHotkeyActions.length) return;

    const matches = registeredHotkeyActions.filter((entry) => eventMatchesActionHotkey(event, entry.hotkey));
    if (!matches.length) return;

    const canRunInsideEditableTarget = matches.some((entry) =>
        entry.hotkey.usesMod || entry.hotkey.ctrl || entry.hotkey.meta || entry.hotkey.alt
    );

    if (isEditableHotkeyTarget(event.target) && !canRunInsideEditableTarget) {
        return;
    }

    const runnableMatches = matches.filter((entry) => !entry.isDisabled());
    if (!runnableMatches.length) return;

    event.preventDefault();

    const uniqueRunnableSignatures = new Set(runnableMatches.map((entry) => entry.signature));

    if (runnableMatches.length !== 1 || uniqueRunnableSignatures.size !== 1) {
        console.warn("Conflicting action hotkey metadata detected.", matches.map((entry) => entry.signature));
        return;
    }

    runnableMatches[0].trigger();
};

const ensureGlobalListener = () => {
    if (isGlobalListenerRegistered || typeof window === "undefined") return;

    window.addEventListener("keydown", handleGlobalKeyDown, true);
    isGlobalListenerRegistered = true;
};

const removeGlobalListenerIfUnused = () => {
    if (!isGlobalListenerRegistered || registeredHotkeyActions.length > 0 || typeof window === "undefined") return;

    window.removeEventListener("keydown", handleGlobalKeyDown, true);
    isGlobalListenerRegistered = false;
};

const useActionHotkeys = ({
    actions,
    disabled = false,
    runningActionId,
    getActionDisabled,
    onActionTrigger,
}: UseActionHotkeysOptions) => {
    const hotkeyEntries = useMemo(() => {
        return actions
            .map((action) => {
                const hotkey = normalizeActionHotkey(getActionShortcut(action));
                return hotkey ? { action, hotkey, signature: hotkeyToSignature(hotkey) } : null;
            })
            .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));
    }, [actions]);

    useEffect(() => {
        if (!hotkeyEntries.length) return;

        const registeredEntries = hotkeyEntries.map((entry) => ({
            ...entry,
            isDisabled: () => disabled || Boolean(runningActionId) || Boolean(getActionDisabled?.(entry.action)),
            trigger: () => onActionTrigger(entry.action),
        }));

        registeredHotkeyActions = [...registeredHotkeyActions, ...registeredEntries];
        ensureGlobalListener();

        return () => {
            registeredHotkeyActions = registeredHotkeyActions.filter((entry) => !registeredEntries.includes(entry));
            removeGlobalListenerIfUnused();
        };
    }, [disabled, getActionDisabled, hotkeyEntries, onActionTrigger, runningActionId]);
};

export default useActionHotkeys;
